import type {
  ApiError,
  DynamicPricingCalendarApartment,
  DynamicPricingCalendarDay,
  DynamicPricingCalendarDayDetailResponse,
} from '@/shared/api';
import { useTranslation } from 'react-i18next';
import { price as money, publicationLabel, stay } from './calendarDisplay';
import { DynamicPricingFact as Fact } from './DynamicPricingFact';
import {
  DynamicPricingOwnershipSection,
  type PricingOwnershipField,
} from './DynamicPricingOwnershipSection';
import { DynamicPricingTechnicalDetails } from './DynamicPricingTechnicalDetails';

export function DynamicPricingDayDetailsBody({
  apartment,
  day,
  detail,
  detailLoading = false,
  detailError = false,
  onReleaseOwnership,
  releasingField,
  releasePending = false,
  releaseError,
}: {
  apartment: DynamicPricingCalendarApartment;
  day: DynamicPricingCalendarDay;
  detail?: DynamicPricingCalendarDayDetailResponse;
  detailLoading?: boolean;
  detailError?: boolean;
  onReleaseOwnership?: (field: PricingOwnershipField) => void;
  releasingField?: PricingOwnershipField;
  releasePending?: boolean;
  releaseError?: ApiError;
}) {
  const { t, i18n } = useTranslation();
  const publication = day.publication;
  const publicationText = publication
    ? `${publicationLabel(publication.status)}${publication.status === 'Sent' ? t('admin.dynamicPricing.calendar.details.confirmationPending') : ''}${publication.lastErrorCode ? ` · ${t('admin.dynamicPricing.calendar.details.publicationError')}` : ''}`
    : t('admin.dynamicPricing.calendar.details.noPublication');
  const feedbackAt = day.confirmedAtUtc
    ? new Intl.DateTimeFormat(i18n.resolvedLanguage, {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: apartment.timezone,
      }).format(new Date(day.confirmedAtUtc))
    : '—';
  return (
    <div className="space-y-5 p-5">
      <section aria-label={t('admin.dynamicPricing.calendar.details.ariCurrent')}>
        <h3 className="text-ink text-sm font-semibold">
          {t('admin.dynamicPricing.calendar.details.ariCurrent')}
        </h3>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <Fact
            label={t('admin.dynamicPricing.calendar.details.currentRate')}
            value={money(day.currentRate ?? null, apartment.currency)}
          />
          <Fact
            label={t('admin.dynamicPricing.calendar.details.currentMinimumStay')}
            value={stay(day.currentMinimumStay ?? null)}
          />
        </div>
      </section>
      <section aria-label={t('admin.dynamicPricing.calendar.details.calculatedTarget')}>
        <h3 className="text-ink text-sm font-semibold">
          {t('admin.dynamicPricing.calendar.details.calculatedTarget')}
        </h3>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <Fact
            label={t('admin.dynamicPricing.calendar.details.targetRate')}
            value={money(day.desiredRate, apartment.currency)}
          />
          <Fact
            label={t('admin.dynamicPricing.calendar.details.targetMinimumStay')}
            value={stay(day.desiredMinimumStay)}
          />
        </div>
      </section>
      <section aria-label={t('admin.dynamicPricing.calendar.details.channexFeedback')}>
        <h3 className="text-ink text-sm font-semibold">
          {t('admin.dynamicPricing.calendar.details.channexFeedback')}
        </h3>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <Fact
            label={t('admin.dynamicPricing.calendar.details.confirmedRate')}
            value={
              day.confirmedRate === null
                ? t('admin.dynamicPricing.calendar.display.noConfirmedRate')
                : money(day.confirmedRate, apartment.currency)
            }
          />
          <Fact
            label={t('admin.dynamicPricing.calendar.details.confirmedMinimumStay')}
            value={
              day.confirmedMinimumStay === null
                ? t('admin.dynamicPricing.calendar.display.noConfirmedMinimumStay')
                : stay(day.confirmedMinimumStay)
            }
          />
          <Fact
            label={t('admin.dynamicPricing.calendar.details.publicationTransport')}
            value={publicationText}
          />
          <Fact label={t('admin.dynamicPricing.calendar.details.confirmedAt')} value={feedbackAt} />
        </div>
      </section>
      <DynamicPricingOwnershipSection
        rateSource={day.rateSource}
        minimumStaySource={day.minimumStaySource}
        onRelease={onReleaseOwnership}
        releasingField={releasingField}
        releasePending={releasePending}
        releaseError={releaseError}
      />
      <DynamicPricingTechnicalDetails
        apartment={apartment}
        day={day}
        detail={detail}
        detailLoading={detailLoading}
        detailError={detailError}
      />
    </div>
  );
}
